import { Extension } from "@tiptap/core";
import Bold from "@tiptap/extension-bold";
import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import HardBreak from "@tiptap/extension-hard-break";
import Heading from "@tiptap/extension-heading";
import History from "@tiptap/extension-history";
import Italic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Strike from "@tiptap/extension-strike";
import Text from "@tiptap/extension-text";
import { EditorContent, useEditor } from "@tiptap/react";
import { Plugin } from "@tiptap/pm/state";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { countStyleGuideWords, STYLE_GUIDE_MAX_WORDS } from "../../../../src/shared/styleGuideWordLimit";
import { PasteFromTextBoxExtension } from "src/lib/tipTapExtensions/pasteFromTextBox";
import { StyleInstructionsFormatToolbar } from "./StyleInstructionsFormatToolbar";
import {
  instructionsMarkdownToHtml,
  serializeInstructionsDoc,
  STYLE_INSTRUCTIONS_PLACEHOLDER,
} from "./styleInstructionsMarkdown";

interface StyleInstructionsEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  onBlur: (markdown: string) => void;
  sourceRef?: React.Ref<HTMLTextAreaElement>;
  wordLimit?: number;
}

export function StyleInstructionsEditor({ value, onChange, onBlur, sourceRef, wordLimit = STYLE_GUIDE_MAX_WORDS }: StyleInstructionsEditorProps) {
  const lastMarkdownRef = useRef(value);
  const applyingExternalValueRef = useRef(false);
  const wordLimitRef = useRef(wordLimit);
  wordLimitRef.current = wordLimit;

  const canApplyEdit = useCallback((markdown: string) => {
    // Generated/external text can already exceed the allowance. Always permit
    // reducing it, and never cut text while loading an existing guide.
    const allowedWords = Math.max(wordLimitRef.current, countStyleGuideWords(lastMarkdownRef.current));
    return countStyleGuideWords(markdown) <= allowedWords;
  }, []);

  const wordLimitExtension = useMemo(() => Extension.create({
    name: "styleInstructionsWordLimit",
    addProseMirrorPlugins() {
      return [new Plugin({
        filterTransaction: (transaction) => {
          if (!transaction.docChanged || applyingExternalValueRef.current) return true;
          return canApplyEdit(serializeInstructionsDoc(transaction.doc));
        },
      })];
    },
  }), [canApplyEdit]);

  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      Heading.configure({ levels: [1, 2, 3] }),
      Bold,
      Italic,
      Strike,
      BulletList,
      OrderedList,
      ListItem,
      HardBreak,
      History,
      Link.configure({ openOnClick: false, autolink: false }),
      Placeholder.configure({ placeholder: STYLE_INSTRUCTIONS_PLACEHOLDER }),
      PasteFromTextBoxExtension,
      wordLimitExtension,
    ],
    immediatelyRender: true,
    content: instructionsMarkdownToHtml(value),
    editorProps: {
      attributes: {
        "data-placeholder": STYLE_INSTRUCTIONS_PLACEHOLDER,
      },
    },
    onUpdate: ({ editor: nextEditor }) => {
      if (applyingExternalValueRef.current) return;
      const markdown = serializeInstructionsDoc(nextEditor.state.doc);
      lastMarkdownRef.current = markdown;
      onChange(markdown);
    },
    onBlur: () => {
      // Focusing an existing guide must not rewrite its Markdown or word count.
      onBlur(lastMarkdownRef.current);
    },
  });

  const applyMarkdown = useCallback(
    (markdown: string) => {
      if (!editor || editor.isDestroyed) return;
      lastMarkdownRef.current = markdown;
      applyingExternalValueRef.current = true;
      editor.commands.setContent(instructionsMarkdownToHtml(markdown), false);
      applyingExternalValueRef.current = false;
    },
    [editor],
  );

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    if (value === lastMarkdownRef.current) return;
    if (editor.isFocused) return;
    applyMarkdown(value);
  }, [applyMarkdown, editor, value]);

  const handleSourceChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const nextValue = event.target.value;
    if (!canApplyEdit(nextValue)) return;
    lastMarkdownRef.current = nextValue;
    onChange(nextValue);
    applyMarkdown(nextValue);
  };

  return (
    <>
      <textarea
        ref={sourceRef}
        aria-hidden="true"
        data-testid="style-editor-instructions-source"
        hidden
        tabIndex={-1}
        value={value}
        onChange={handleSourceChange}
        onBlur={(event) => onBlur(event.currentTarget.value)}
      />
      <div aria-label="Instructions" aria-multiline="true" role="textbox">
        <EditorContent editor={editor} />
        <StyleInstructionsFormatToolbar editor={editor} />
      </div>
    </>
  );
}
